import { alpha, createTheme } from '@mui/material/styles';

export const resolveThemeMode = (themePreference, systemMode = 'light') => {
  if (themePreference === 'dark' || themePreference === 'light') {
    return themePreference;
  }

  return systemMode === 'dark' ? 'dark' : 'light';
};

export const createAppTheme = (mode = 'light') => {
  const resolvedMode = mode === 'dark' ? 'dark' : 'light';
  const isDark = resolvedMode === 'dark';

  const primaryPalette = isDark
    ? {
      50: alpha('#8EA2E8', 0.18),
      main: '#8EA2E8',
      light: '#B6C4F4',
      dark: '#5E74C4',
      contrastText: '#0D1427',
    }
    : {
      50: '#E8ECFA',
      main: '#1A2B57',
      light: '#2E4175',
      dark: '#111C3A',
      contrastText: '#FFFFFF',
    };

  const secondaryPalette = isDark
    ? {
      main: '#A4B2F4',
      light: '#C0CAF9',
      dark: '#6F82DB',
      contrastText: '#0D1427',
    }
    : {
      main: '#5C6BC0',
      light: '#8E99D6',
      dark: '#3F4C8C',
      contrastText: '#FFFFFF',
    };

  const greyPalette = isDark
    ? {
      50: '#1A2746',
      100: '#223055',
      200: '#2C3B63',
      300: '#3A4A72',
      400: '#4D5E86',
      500: '#6B7AA0',
      600: '#8A97B8',
      700: '#ABB6D0',
      800: '#CCD4E6',
      900: '#E8EDF8',
    }
    : {
      50: '#F7F9FC',
      100: '#EEF2F8',
      200: '#D9E1EF',
      300: '#BAC8DE',
      400: '#8EA0C0',
      500: '#697C9C',
      600: '#526380',
      700: '#3E4D67',
      800: '#2D3A51',
      900: '#1C2738',
    };

  const primaryHover = isDark ? alpha(primaryPalette.main, 0.22) : alpha(primaryPalette.main, 0.08);

  return createTheme({
    palette: {
      mode: resolvedMode,
      primary: primaryPalette,
      secondary: secondaryPalette,
      background: {
        default: isDark ? '#0D1427' : '#DADFF1',
        paper: isDark ? '#15203A' : '#FFFFFF',
        sidebar: isDark ? '#0B1223' : '#162447',
      },
      grey: greyPalette,
      text: {
        primary: isDark ? '#E6ECFF' : '#1A2B57',
        secondary: isDark ? '#A9B4D6' : '#353E4F',
      },
      action: {
        hover: primaryHover,
        selected: isDark ? alpha(primaryPalette.main, 0.26) : alpha(primaryPalette.main, 0.12),
        focus: isDark ? alpha(primaryPalette.main, 0.3) : alpha(primaryPalette.main, 0.18),
        disabled: isDark ? alpha('#D6DFFF', 0.52) : alpha('#1A2B57', 0.38),
        disabledBackground: isDark ? alpha('#8EA2E8', 0.22) : alpha('#1A2B57', 0.12),
      },
      error: {
        main: '#D32F2F',
      },
      warning: {
        main: '#ED6C02',
      },
      success: {
        main: '#2E7D32',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: '1rem',
      },
      body2: {
        fontSize: '0.875rem',
      },
      button: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            '&.Mui-disabled': {
              color: isDark ? alpha('#D6DFFF', 0.68) : undefined,
            },
          },
          outlined: {
            borderColor: isDark ? alpha('#A9B4D6', 0.5) : undefined,
            '&.Mui-disabled': {
              borderColor: isDark ? alpha('#A9B4D6', 0.35) : undefined,
            },
          },
          contained: {
            '&.Mui-disabled': {
              backgroundColor: isDark ? alpha('#8EA2E8', 0.24) : undefined,
              color: isDark ? alpha('#E6ECFF', 0.75) : undefined,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 999,
            backgroundColor: isDark ? '#8EA2E8' : '#1A2B57',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: isDark ? '#8EA0C0' : '#526380',
            '&.Mui-selected': {
              color: isDark ? '#E6ECFF' : '#1A2B57',
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: isDark ? alpha('#A9B4D6', 0.45) : alpha('#1A2B57', 0.2),
            borderTopWidth: 2,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: isDark ? '#9CADCF' : undefined,
            '&.Mui-active': {
              color: isDark ? '#E6ECFF' : undefined,
            },
            '&.Mui-completed': {
              color: isDark ? '#C6D2EE' : undefined,
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: isDark ? alpha('#A9B4D6', 0.45) : undefined,
            '&.Mui-active': {
              color: isDark ? primaryPalette.main : undefined,
            },
            '&.Mui-completed': {
              color: isDark ? '#4CAF50' : undefined,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: isDark ? '#D6DFFF' : '#FFFFFF',
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: isDark ? '#5870C8' : '#1A2B57',
              opacity: 1,
            },
          },
          thumb: {
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          },
          track: {
            borderRadius: 999,
            backgroundColor: isDark ? '#42507A' : '#9AA8C4',
            opacity: 1,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          outlined: {
            borderColor: isDark ? alpha('#A9B4D6', 0.45) : undefined,
            color: isDark ? '#D5DDF5' : undefined,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isDark ? '#C7D2EE' : undefined,
            '&.Mui-disabled': {
              color: isDark ? alpha('#C7D2EE', 0.35) : undefined,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: isDark ? alpha('#8EA2E8', 0.18) : alpha('#1A2B57', 0.06),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? alpha('#A9B4D6', 0.2) : alpha('#1A2B57', 0.12),
          },
        },
      },
    },
    shape: {
      borderRadius: 4,
    },
  });
};

const theme = createAppTheme('light');

export default theme;
